import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { WHATSAPP_URL } from "../lib/constants";

/**
 * Ad delivery cascade:
 *   1. Custom Orion ad (from /api/ads/serve/:slot) — tracked views/clicks
 *   2. Google AdSense placeholder — only if `adsense_locked === false`
 *   3. WhatsApp CTA fallback — only when adsense is locked AND no house ad
 *
 * Rendering: banner shows at its NATURAL aspect ratio (object-contain) to
 * never crop the uploaded creative. The slot's IAB spec is used only as a
 * max-width guide so the banner never grows beyond its intended container.
 */
const SLOT_SPECS = {
  "home-mid":       { maxW: 728, minH: 90,  mobileMaxW: 320 },
  "article-top":    { maxW: 728, minH: 90,  mobileMaxW: 320 },
  "article-bottom": { maxW: 336, minH: 280, mobileMaxW: 300 },
  "sidebar":        { maxW: 300, minH: 600, mobileMaxW: 300 },
  "footer":         { maxW: 728, minH: 90,  mobileMaxW: 320 },
};

const CTA_PHONE_DISPLAY = "(11) 92004-3024";
const CTA_PHONE_WA = "5511920043024";

export default function AdSlot({ label = "Publicidade", id, height = 250 }) {
  const [ad, setAd] = useState(null);
  const [adsenseLocked, setAdsenseLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [closed, setClosed] = useState(false); // user clicked the X — show CTA state
  const [dismissedForever, setDismissedForever] = useState(false); // user clicked "Fechar permanentemente"
  const viewedRef = useRef(false);

  // Persistent "dismiss" — se o usuário fechou permanentemente esse slot num
  // dispositivo, respeitamos por 30 dias (via localStorage).
  useEffect(() => {
    try {
      const key = `orion_ad_dismiss_${id}`;
      const v = window.localStorage.getItem(key);
      if (v) {
        const ts = parseInt(v, 10);
        if (Number.isFinite(ts) && Date.now() - ts < 30 * 24 * 60 * 60 * 1000) {
          setDismissedForever(true);
        } else {
          window.localStorage.removeItem(key);
        }
      }
    } catch {}
  }, [id]);

  const dismissForever = () => {
    try {
      window.localStorage.setItem(`orion_ad_dismiss_${id}`, String(Date.now()));
    } catch {}
    setDismissedForever(true);
  };

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      return;
    }
    api
      .get(`/ads/serve/${id}`)
      .then((r) => {
        if (cancelled) return;
        setAd(r.data?.ad || null);
        setAdsenseLocked(!!r.data?.adsense_locked);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fire a view event exactly once per mount when an ad is displayed
  useEffect(() => {
    if (ad?.id && !viewedRef.current) {
      viewedRef.current = true;
      api.post(`/ads/${ad.id}/view`).catch(() => {});
    }
  }, [ad?.id]);

  // Loading spacer to prevent layout shift
  if (loading) {
    return (
      <div
        className="ad-slot w-full my-10"
        style={{ minHeight: height }}
        data-testid={`ad-slot-${id || label}`}
        aria-hidden="true"
      />
    );
  }

  // Se o usuário fechou permanentemente esse slot, não mostra NADA — some.
  if (dismissedForever) return null;

  // 1) Custom house ad — ALWAYS shows the image; custom_html is used only
  //    as the click destination (rendered by the backend at /api/go/{slug}).
  if (ad) {
    const spec = SLOT_SPECS[id] || SLOT_SPECS["article-bottom"];
    return (
      <AdContainer id={id} spec={spec} ad={ad} closed={closed} setClosed={setClosed} onDismissForever={dismissForever}>
        <a
          href={ad.link}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          aria-label={ad.titulo}
          className="block"
          data-testid={`ad-slot-${id}-custom`}
          style={{ maxWidth: `${spec.maxW}px`, width: "100%" }}
        >
          {/* Desktop image */}
          <img
            src={ad.imagem_desktop}
            alt={ad.titulo}
            loading="lazy"
            decoding="async"
            className="hidden sm:block w-full h-auto object-contain mx-auto"
            style={{ maxWidth: `${spec.maxW}px` }}
          />
          {/* Mobile image — falls back to desktop when not provided */}
          <img
            src={ad.imagem_mobile || ad.imagem_desktop}
            alt={ad.titulo}
            loading="lazy"
            decoding="async"
            className="block sm:hidden w-full h-auto object-contain mx-auto"
            style={{ maxWidth: `${spec.mobileMaxW}px` }}
          />
        </a>
      </AdContainer>
    );
  }

  // 2) AdSense placeholder (only when NOT locked). Uses a small compact
  //    banner so an empty slot never eats content visibility.
  if (!adsenseLocked) {
    return (
      <div
        className="ad-slot w-full my-8 text-[10px] uppercase tracking-[0.3em] text-neutral-400 text-center py-3 border border-dashed border-neutral-300"
        data-testid={`ad-slot-${id || label}`}
        aria-label="Espaço reservado para publicidade"
      >
        {label}
      </div>
    );
  }

  // 3) AdSense locked and no house ad — WhatsApp fallback CTA
  return (
    <div className="my-10 w-full flex justify-center" data-testid={`ad-slot-${id}-fallback`}>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        className="max-w-md w-full bg-orion-purple text-white px-6 py-6 flex flex-col items-center gap-2 hover:bg-[#3D2A56] transition-colors"
        aria-label="Fale com a Viagens Orion no WhatsApp"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-orion-gold">Viagens Orion</span>
        <span className="font-serif text-lg text-center leading-tight">
          Cansado do óbvio? Monte seu roteiro sob medida.
        </span>
        <span className="text-xs uppercase tracking-widest text-orion-orange">Fale no WhatsApp →</span>
      </a>
    </div>
  );
}

/**
 * AdContainer wraps the actual banner (image or iframe) with:
 *   • a discreet X button (top-right) that swaps the banner for a "Anuncie
 *     aqui" invitation. The user can restore the banner via a small "Voltar a
 *     ver este anúncio" link.
 *   • the "Publi" badge (bottom-right) so we're compliant with disclosure rules.
 */
function AdContainer({ id, spec, ad, closed, setClosed, onDismissForever, children }) {
  if (closed) {
    return (
      <div className="my-10 w-full flex justify-center px-3 sm:px-0" data-testid={`ad-slot-${id}-closed`}>
        <div
          className="w-full bg-[#F8F5EE] border border-[#DFD8C7] p-5 text-center"
          style={{ maxWidth: `${spec.maxW}px` }}
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-orion-orange font-semibold mb-2">
            Conteúdo Verificado e Patrocinado
          </div>
          <div className="text-sm text-orion-purple leading-snug mb-4">
            Este anúncio ajuda a manter o Viagens Orion gratuito.
          </div>
          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-2">
            <a
              href={`https://wa.me/${CTA_PHONE_WA}?text=${encodeURIComponent("Olá! Quero anunciar no Viagens Orion da Viagens Orion.")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1 bg-orion-orange text-white px-4 py-2 text-[11px] uppercase tracking-widest font-bold hover:bg-[#E04828] transition-colors"
              data-testid={`ad-slot-${id}-anuncie`}
            >
              Anunciar aqui · {CTA_PHONE_DISPLAY}
            </a>
            <button
              type="button"
              onClick={() => setClosed(false)}
              className="inline-flex items-center justify-center gap-1 border border-orion-purple text-orion-purple px-4 py-2 text-[11px] uppercase tracking-widest font-semibold hover:bg-orion-purple hover:text-white transition-colors"
              data-testid={`ad-slot-${id}-restore`}
            >
              Voltar ao anúncio
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="my-10 w-full flex justify-center px-3 sm:px-0" data-testid={`ad-slot-${id}`}>
      <div className="relative inline-block" style={{ maxWidth: `${spec.maxW}px`, width: "100%" }}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setClosed(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Fechar este anúncio"
          title="Fechar este anúncio"
          className="ad-slot-close-btn absolute top-1 right-1 z-30 w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-black/80 hover:bg-black text-white flex items-center justify-center shadow-lg border-2 border-white transition-colors cursor-pointer"
          data-testid={`ad-slot-${id}-close`}
        >
          <X size={14} strokeWidth={2.5} />
        </button>
        {children}
        <span className="absolute bottom-1 right-1 bg-black/50 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded pointer-events-none">
          Publi
        </span>
      </div>
    </div>
  );
}
